import { ProductContext } from "@database/contexts";
import { useCallback, useContext, useState } from "react";
import { useOrganisation } from "./useOrganisation";
import { 
    AddProductFormType, AddSupplyInputType, 
    ErrorType, UpdateProductFormType,
} from "@utilities/types";
import { ProductService } from "@database/services";

export const useProduct = () => {
   
    const { 
        products, setProducts,
        productsPagination, setProductsPagination,
    } = useContext(ProductContext);
    const { organisation } = useOrganisation();

    const [getAllOrganisationProductsLoading, setGetAllOrganisationProductsLoading] = useState<boolean>(false);
    const [getAllOrganisationProductsByCategoryLoading, setGetAllOrganisationProductsByCategoryLoading] = useState<boolean>(false);
    const [searchProductsLoading, setSearchProductsLoading] = useState<boolean>(false);
    const [createProductLoading, setCreateProductLoading] = useState<boolean>(false);
    const [updateProductLoading, setUpdateProductLoading] = useState<boolean>(false);
    const [supplyProductLoading, setSupplyProductLoading] = useState<boolean>(false);
    const [deleteProductLoading, setDeleteProductLoading] = useState<boolean>(false);
    const [getAllOrganisationProductsError, setGetAllOrganisationProductsError] = useState<ErrorType | null>(null);
    const [getAllOrganisationProductsByCategoryError, setGetAllOrganisationProductsByCategoryError] = useState<ErrorType | null>(null);
    const [searchProductsError, setSearchProductsError] = useState<ErrorType | null>(null);
    const [createProductError, setCreateProductError] = useState<ErrorType | null>(null);
    const [updateProductError, setUpdateProductError] = useState<ErrorType | null>(null);
    const [supplyProductError, setSupplyProductError] = useState<ErrorType | null>(null);
    const [deleteProductError, setDeleteProductError] = useState<ErrorType | null>(null);
    const [createProductSuccess, setCreateProductSuccess] = useState<string>('');

    const onGetAllOrganisationProducts = useCallback(async (page = 1, isProductManagement?: boolean) => {
        if (!organisation) return;

        setGetAllOrganisationProductsLoading(true);
        const req = await ProductService.getAllOrganisationProducts(organisation._id, page, 10, isProductManagement);

        if (req.code === 200) {
            setProducts(req.data.data);
            setProductsPagination({
                currentPage: req.data.currentPage,
                totalPages: req.data.totalPages,
                totalItems: req.data.totalItems,
            });
            setGetAllOrganisationProductsError(null);
        } else {
            setProducts([]);
            setProductsPagination(null);
            setGetAllOrganisationProductsError({
                message: "Une erreur est survenue, veuillez réessayer",
            });
        }
        setGetAllOrganisationProductsLoading(false);
    }, [organisation, setProducts, setProductsPagination]);

    const onGetAllOrganisationProductsByCategory = useCallback(async (categoryId: string, page = 1, isProductManagement?: boolean) => {
        if (!organisation) return;

        setGetAllOrganisationProductsByCategoryLoading(true);
        const req = await ProductService.getAllProductsByCategory(categoryId, page, 10, isProductManagement);

        if (req.code === 200) {
            setProducts(req.data.data);
            setProductsPagination({
                currentPage: req.data.currentPage,
                totalPages: req.data.totalPages,
                totalItems: req.data.totalItems,
            });
            setGetAllOrganisationProductsByCategoryError(null);
        } else {
            setProducts([]);
            setProductsPagination(null);
            setGetAllOrganisationProductsByCategoryError({
                message: "Une erreur est survenue, veuillez réessayer",
            });
        }
        setGetAllOrganisationProductsByCategoryLoading(false);
    }, [organisation, setProducts, setProductsPagination]);

    const onCreateProduct = useCallback(async (product: AddProductFormType, images: File[]) => {
        if (!organisation) return;

        setCreateProductLoading(true);
        setCreateProductSuccess('');
        const req = await ProductService.addProduct({
            ...product,
            organisationID: organisation._id,
        }, images);

        if (req.code === 201) {
            await onGetAllOrganisationProducts();
            setCreateProductError(null);
            setCreateProductSuccess('Produit créé avec succès');
        } else {
            setCreateProductError({
                message: req.code === 400 ?
                    "Données invalides." :
                    "Une erreur est survenue, veuillez réessayer",
            });
        }
        setCreateProductLoading(false);
    }, [organisation, onGetAllOrganisationProducts]);

    const onUpdateProduct = useCallback(async (productId: string, product: UpdateProductFormType) => {
        if (!organisation) return;

        setUpdateProductLoading(true);
        const req = await ProductService.updateProduct(productId, product);

        if (req.code === 200) {
            setUpdateProductError(null);
        } else {
            setUpdateProductError({
                message: req.code === 400 ?
                    "Données invalides." :
                    "Une erreur est survenue, veuillez réessayer",
            });
        }
        setUpdateProductLoading(false);
    }, [organisation]);

    const onSupplyProduct = useCallback(async (productId: string, supply: AddSupplyInputType) => {

        setSupplyProductLoading(true);
        const req = await ProductService.supplyProduct(productId, supply);

        if (req.code === 200) {
            await onGetAllOrganisationProducts();
            setSupplyProductError(null);
        } else {
            setSupplyProductError({
                message: req.code === 400 ?
                    "Données invalides." :
                    "Une erreur est survenue, veuillez réessayer",
            });
        }
        setSupplyProductLoading(false);
    }, [onGetAllOrganisationProducts]);

    const onDeleteProduct = useCallback(async (productId: string) => {

        setDeleteProductLoading(true);
        const req = await ProductService.deleteProduct(productId);

        if (req.code === 200) {
            await onGetAllOrganisationProducts();
            setDeleteProductError(null);
        } else {
            setDeleteProductError({
                message: req.code === 404 ?
                    "Le produit n'existe pas" :
                    "Une erreur est survenue, veuillez réessayer",
            });
        }
        setDeleteProductLoading(false);
    }, [onGetAllOrganisationProducts]);

    const onSearchProducts = useCallback(async (searchTerm: string, page = 1) => {
        if (!organisation || !searchTerm.trim()) return;

        setSearchProductsLoading(true);
        const req = await ProductService.searchProducts(organisation._id, searchTerm, page, 10);

        if (req.code === 200) {
            setProducts(req.data.data.products);
            setProductsPagination({
                currentPage: req.data.data.pagination.currentPage,
                totalPages: req.data.data.pagination.totalPages,
                totalItems: req.data.data.pagination.totalProducts,
            });
            setSearchProductsError(null);
        } else {
            setProducts([]);
            setProductsPagination(null);
            setSearchProductsError({
                message: "Une erreur est survenue lors de la recherche, veuillez réessayer",
            });
        }
        setSearchProductsLoading(false);
    }, [organisation, setProducts, setProductsPagination]);

    return {
        createProductSuccess, productsPagination,
        products, getAllOrganisationProductsLoading, getAllOrganisationProductsByCategoryLoading,
        searchProductsLoading, createProductLoading, updateProductLoading, supplyProductLoading, deleteProductLoading,
        getAllOrganisationProductsError, getAllOrganisationProductsByCategoryError, searchProductsError,
        createProductError, updateProductError, supplyProductError, deleteProductError,
        onGetAllOrganisationProducts, onGetAllOrganisationProductsByCategory, onSearchProducts,
        onCreateProduct, onUpdateProduct, onSupplyProduct, onDeleteProduct,
        setProducts,
    };
};
